import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { supabase } from '../lib/supabase';
import { fetchCruiseSettings } from '../hooks/useCruiseSettings';
import { fetchAllCruiseTimes } from '../utils/cruiseDetails';
import { AlertTriangle, Camera, Lock, Pin } from 'lucide-react';

const NAVY = '#0A1628';
const GOLD = '#C9A84C';
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const ordinal = (n) => { const s = ['th','st','nd','rd']; const v = n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
const toDisplayDate = (yyyymmdd) => { if (!yyyymmdd) return ''; const [y,m,d] = yyyymmdd.split('-'); return `${d}-${m}-${y}`; };
const formatDateWords = (yyyymmdd) => { if (!yyyymmdd) return ''; const [y,m,d] = yyyymmdd.split('-').map(Number); return `${ordinal(d)} ${MONTHS[m-1]} ${y}`; };
// Local-timezone today (toISOString is UTC — before 05:30 IST it returns yesterday)
const todayInput = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const fmtTime = (ts) => { if (!ts) return ''; const d = new Date(ts); return d.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true }); };

// boardedCache: Map<bookingId, { booking, tables, checkedInAt }>
// Kept in a ref on ScannerScreen so same-session rescans are instant.
async function processQR(refText, inputDate, cruise, boardedCache) {
  const displayDate = toDisplayDate(inputDate);
  const ref = refText.trim();

  const { data: refData } = await supabase
    .from('booking_references')
    .select('booking_id')
    .eq('reference_number', ref)
    .single();

  if (!refData?.booking_id) return { type: 'not_found', ref };

  const bookingId = refData.booking_id;

  // ── 1. Local memory check — no DB round-trip needed ──────────────────────────
  if (boardedCache.has(bookingId)) {
    const cached = boardedCache.get(bookingId);
    return { type: 'already_boarded', booking: cached.booking, checkedInAt: cached.checkedInAt, tables: cached.tables, tableLabel: cached.tableLabel };
  }

  // ── 2. Fetch booking + table assignments ──────────────────────────────────────
  const [bookingRes, tablesRes] = await Promise.all([
    supabase.from('bookings').select('*').eq('id', bookingId).single(),
    supabase.from('seating_assignments')
      .select('table_name, is_rain')
      .eq('booking_id', bookingId)
      .eq('date', displayDate)
      .eq('cruise_type', cruise.cruise_name),
  ]);

  const booking = bookingRes.data;
  if (!booking) return { type: 'not_found', ref };

  // All tables (own + rain) get boarded; the label separates the real
  // allocation from temporary rain seating so it never reads as a wrong table.
  const tableRows = tablesRes.data || [];
  const tables = tableRows.map(t => t.table_name);
  const rainT  = tableRows.filter(t => t.is_rain).map(t => t.table_name);
  const ownT   = tableRows.filter(t => !t.is_rain).map(t => t.table_name);
  const tableLabel = tables.length
    ? (rainT.length ? `${ownT.join(', ') || '—'} · Rain: ${rainT.join(', ')}` : tables.join(', '))
    : '';

  if (booking.payment_status === 'Cancelled') return { type: 'cancelled', booking };

  if (booking.date !== displayDate || booking.cruise_type !== cruise.cruise_name) {
    return { type: 'wrong_cruise', booking, selectedDate: inputDate, selectedCruise: cruise.cruise_name };
  }

  // ── 3. DB check — catches restarts or cross-device scans ──────────────────────
  const { data: checkinRow } = await supabase
    .from('booking_checkins')
    .select('checked_in_at')
    .eq('booking_id', bookingId)
    .eq('date', displayDate)
    .eq('cruise_type', cruise.cruise_name)
    .single();

  if (checkinRow) {
    const entry = { booking, tables, tableLabel, checkedInAt: checkinRow.checked_in_at };
    boardedCache.set(bookingId, entry);
    return { type: 'already_boarded', ...entry };
  }

  // ── 4. First valid scan — write all records ───────────────────────────────────
  const now = new Date().toISOString();
  const writes = [
    supabase.from('booking_checkins').insert({
      booking_id: bookingId,
      date: displayDate,
      cruise_type: cruise.cruise_name,
    }),
    ...tables.map(tableName =>
      supabase.from('boarded_status').upsert(
        { table_name: tableName, date: displayDate, cruise_type: cruise.cruise_name, boarded: true, boarded_at: now },
        { onConflict: 'table_name,date,cruise_type' }
      )
    ),
  ];
  await Promise.all(writes);

  // Cache immediately so next scan is instant
  boardedCache.set(bookingId, { booking, tables, tableLabel, checkedInAt: now });

  return { type: 'success', booking, ref, tables, tableLabel };
}

// ─── Result overlay ───────────────────────────────────────────────────────────

function ResultOverlay({ result, onNext }) {
  const cfg = {
    success:       { bg: '#15803d', icon: '✓', title: 'BOARDED' },
    already_boarded:{ bg: '#B45309', icon: AlertTriangle, title: 'ALREADY BOARDED' },
    wrong_cruise:  { bg: '#b91c1c', icon: '✗', title: 'INVALID TICKET' },
    cancelled:     { bg: '#b91c1c', icon: '✗', title: 'CANCELLED BOOKING' },
    not_found:     { bg: '#b91c1c', icon: '✗', title: 'NOT FOUND' },
  }[result.type] || { bg: '#b91c1c', icon: '✗', title: 'ERROR' };

  const { booking } = result;

  return (
    <div
      onClick={onNext}
      style={{
        position: 'fixed', inset: 0,
        background: cfg.bg,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        zIndex: 500, padding: 32, cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <div style={{ fontSize: '4rem', lineHeight: 1, marginBottom: 10, color: '#fff', display: 'flex' }}>{typeof cfg.icon === 'string' ? cfg.icon : <cfg.icon size={56} strokeWidth={1.75} aria-hidden="true" />}</div>
      <div style={{ fontSize: '2rem', fontWeight: 900, color: '#fff', letterSpacing: '2px', marginBottom: 20 }}>{cfg.title}</div>

      {result.type === 'success' && booking && (
        <>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff', marginBottom: 6, textAlign: 'center' }}>{booking.booking_name}</div>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.85)', marginBottom: 4 }}>{booking.cruise_type} — {booking.deck}</div>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.85)', marginBottom: 4 }}>
            Table: {result.tableLabel || (result.tables?.length ? result.tables.join(', ') : 'Unassigned')}
          </div>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>
            Adults: {booking.adults || 0}{booking.children > 0 ? ` | Children: ${booking.children}` : ''}
          </div>
          <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>{result.ref}</div>
        </>
      )}

      {result.type === 'already_boarded' && booking && (
        <>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff', marginBottom: 6, textAlign: 'center' }}>{booking.booking_name}</div>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.85)', marginBottom: 4, textAlign: 'center' }}>{booking.cruise_type} — {booking.deck}</div>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.85)', marginBottom: 4, textAlign: 'center' }}>
            Table: {result.tableLabel || (result.tables?.length ? result.tables.join(', ') : 'Unassigned')}
          </div>
          <div style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.75)', marginTop: 8, textAlign: 'center' }}>
            Boarded at: {fmtTime(result.checkedInAt)}
          </div>
        </>
      )}

      {result.type === 'wrong_cruise' && booking && (
        <>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.75)', marginBottom: 16, textAlign: 'center', lineHeight: 1.6 }}>
            This ticket is for:<br />
            <strong style={{ color: '#fff' }}>{booking.cruise_type}</strong> on <strong style={{ color: '#fff' }}>{booking.date}</strong>
          </div>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.75)', textAlign: 'center', lineHeight: 1.6 }}>
            You are checking in:<br />
            <strong style={{ color: '#fff' }}>{result.selectedCruise}</strong> on <strong style={{ color: '#fff' }}>{toDisplayDate(result.selectedDate)}</strong>
          </div>
        </>
      )}

      {result.type === 'cancelled' && booking && (
        <>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff', marginBottom: 6, textAlign: 'center' }}>{booking.booking_name}</div>
          <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.75)', textAlign: 'center' }}>This booking has been cancelled.</div>
        </>
      )}

      {result.type === 'not_found' && (
        <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', textAlign: 'center', marginTop: 4 }}>
          Reference: {result.ref}<br />No booking found for this reference.
        </div>
      )}

      <div style={{
        marginTop: 40,
        background: 'rgba(255,255,255,0.2)',
        color: '#fff',
        border: '2px solid rgba(255,255,255,0.4)',
        borderRadius: 14,
        padding: '14px 36px',
        fontSize: '1rem',
        fontWeight: 700,
        letterSpacing: '0.5px',
      }}>
        Tap to scan next
      </div>
    </div>
  );
}

// ─── Manual reference entry ───────────────────────────────────────────────────
// Fallback for when the QR genuinely cannot be scanned (cracked screen, glare,
// dead phone, reference read out over a call). Deliberately tucked behind a
// small link and framed as the exception: scanning stays the habit, typing is
// the escape hatch. Feeds the exact same processQR path as the camera.

function ManualEntryOverlay({ onSubmit, onCancel, busy }) {
  const [ref, setRef] = useState('');
  const cleaned = ref.replace(/\s+/g, '').toUpperCase();
  const canSubmit = cleaned.length >= 6 && !busy;

  const submit = () => {
    if (canSubmit) onSubmit(cleaned);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 400, padding: 24,
    }}>
      <div style={{
        background: NAVY, border: `1px solid ${GOLD}55`, borderRadius: 16,
        padding: '22px 20px', width: '100%', maxWidth: 380,
      }}>
        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>
          Manual check-in
        </div>
        <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, marginBottom: 16 }}>
          Only for tickets that cannot be scanned. Type the booking reference exactly as printed on the ticket.
        </div>
        <input
          autoFocus
          value={ref}
          onChange={(e) => setRef(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="SS-015-080826"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          disabled={busy}
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 10, padding: '13px 14px',
            color: '#fff', fontSize: '1.05rem', fontWeight: 700,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            letterSpacing: '1px', textAlign: 'center', outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              flex: 1, background: 'none', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 10, padding: '13px 0', color: 'rgba(255,255,255,0.75)',
              fontSize: '0.92rem', fontWeight: 700, cursor: busy ? 'default' : 'pointer',
            }}
          >
            Back to scanning
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            style={{
              flex: 1, background: canSubmit ? GOLD : 'rgba(255,255,255,0.12)',
              border: 'none', borderRadius: 10, padding: '13px 0',
              color: canSubmit ? NAVY : 'rgba(255,255,255,0.35)',
              fontSize: '0.92rem', fontWeight: 800, cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Checking…' : 'Check in'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Scanner screen ───────────────────────────────────────────────────────────

function ScannerScreen({ inputDate, cruise, onBack }) {
  const isProcessingRef = useRef(false);
  const scannerRef = useRef(null);
  const boardedCacheRef = useRef(new Map()); // bookingId → { booking, tables, checkedInAt }
  const [result, setResult] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);

  // While the manual dialog is open the camera keeps running behind it, so
  // isProcessingRef doubles as the guard that stops a stray scan from firing a
  // result underneath the dialog. handleNextScan releases it as usual.
  const openManual = () => {
    isProcessingRef.current = true;
    setManualOpen(true);
  };
  const cancelManual = () => {
    setManualOpen(false);
    isProcessingRef.current = false;
  };
  const submitManual = async (ref) => {
    setManualBusy(true);
    try {
      const r = await processQR(ref, inputDate, cruise, boardedCacheRef.current);
      setResult(r);
    } catch (err) {
      setResult({ type: 'not_found', ref });
    }
    setManualBusy(false);
    setManualOpen(false);
  };

  useEffect(() => {
    let stopped = false;
    const qr = new Html5Qrcode('ci-qr-reader');
    scannerRef.current = qr;

    qr.start(
      { facingMode: 'environment' },
      {
        fps: 25,
        qrbox: (w, h) => { const side = Math.round(Math.min(w, h) * 0.8); return { width: side, height: side }; },
        videoConstraints: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      },
      async (decoded) => {
        if (isProcessingRef.current || stopped) return;
        isProcessingRef.current = true;
        try {
          const r = await processQR(decoded, inputDate, cruise, boardedCacheRef.current);
          if (!stopped) setResult(r);
        } catch (err) {
          if (!stopped) setResult({ type: 'not_found', ref: decoded });
        }
      },
      () => {}
    )
      .then(() => { if (!stopped) setLoading(false); })
      .catch(() => {
        if (!stopped) setCameraError('Camera access denied or unavailable.\nPlease allow camera access in your browser settings to scan QR codes.');
      });

    return () => {
      stopped = true;
      if (qr.isScanning) qr.stop().catch(() => {});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNextScan = () => {
    setResult(null);
    isProcessingRef.current = false;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        background: NAVY, padding: 'calc(env(safe-area-inset-top) + 10px) 16px 10px',
        display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, zIndex: 10,
        borderBottom: `2px solid ${GOLD}44`,
      }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', padding: '6px 0', flexShrink: 0 }}
        >← Back</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.7rem', color: GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Scanning for</div>
          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cruise.cruise_name} · {toDisplayDate(inputDate)}
          </div>
        </div>
      </div>

      {/* Camera area */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <div id="ci-qr-reader" style={{ width: '100%', height: '100%' }} />

        {loading && !cameraError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#111', gap: 16 }}>
            <div style={{ width: 40, height: 40, border: `4px solid ${GOLD}44`, borderTop: `4px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>Starting camera…</div>
          </div>
        )}

        {cameraError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#111', gap: 20, padding: 32 }}>
            <div style={{ color: '#fff', display: 'flex' }}><Camera size={48} strokeWidth={1.75} aria-hidden="true" /></div>
            <div style={{ color: '#fff', fontSize: '1rem', fontWeight: 600, textAlign: 'center', whiteSpace: 'pre-line', lineHeight: 1.6 }}>{cameraError}</div>
          </div>
        )}
      </div>

      {/* Status bar */}
      {!cameraError && !loading && !result && (
        <div style={{ background: NAVY, padding: '12px 16px 14px', textAlign: 'center', flexShrink: 0 }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>Point the camera at a boarding pass QR code</div>
          <button
            onClick={openManual}
            style={{
              background: 'none', border: 'none', marginTop: 6, padding: '4px 8px',
              color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontWeight: 600,
              textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer',
            }}
          >
            Code unreadable? Enter the reference
          </button>
        </div>
      )}

      {/* Manual entry — camera error path gets it too (it may be the ONLY way in) */}
      {cameraError && !result && (
        <div style={{ background: NAVY, padding: '12px 16px 14px', textAlign: 'center', flexShrink: 0 }}>
          <button
            onClick={openManual}
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10,
              padding: '12px 20px', color: '#fff', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Enter reference manually
          </button>
        </div>
      )}

      {manualOpen && !result && (
        <ManualEntryOverlay onSubmit={submitManual} onCancel={cancelManual} busy={manualBusy} />
      )}

      {/* Result overlay */}
      {result && <ResultOverlay result={result} onNext={handleNextScan} />}
    </div>
  );
}

// ─── Select screen (date + cruise) ───────────────────────────────────────────

function SelectScreen({ onStart, onExit }) {
  // Check-in is a live boarding activity — the date is hard-locked to TODAY.
  const [cruises, setCruises] = useState([]);
  const [selectedCruise, setSelectedCruise] = useState(null);

  useEffect(() => {
    // The cruise list (which cruises, decks, order) comes from the settings
    // hook; the times shown under each name come from the shared
    // cruiseDetails lookup for TODAY, the date check-in is locked to.
    (async () => {
      const list = await fetchCruiseSettings();
      const times = await fetchAllCruiseTimes({ date: todayInput() });
      const merged = list.map((c) => {
        const t = times[c.cruise_name];
        return t ? { ...c, departure_time: t.departure_time, end_time: t.end_time } : c;
      });
      setCruises(merged);
      if (merged.length === 1) setSelectedCruise(merged[0]);
    })();
  }, []);

  const canStart = !!selectedCruise;

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--color-canvas)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: NAVY, padding: 'calc(env(safe-area-inset-top) + 14px) 20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `2px solid ${GOLD}44` }}>
        <div>
          <div style={{ fontSize: '0.65rem', color: GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Alfresco Grand</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>Check-in</div>
        </div>
        <button
          onClick={onExit}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 7, color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', fontWeight: 600, padding: '6px 14px', cursor: 'pointer' }}
        >
          Back to app
        </button>
      </div>

      <div style={{ flex: 1, padding: '24px 20px', maxWidth: 480, width: '100%', margin: '0 auto' }}>
        {/* Date — hard-locked to today (check-in is live boarding only) */}
        <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 20px', marginBottom: 18, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</span>
            <Lock size={11} strokeWidth={2} aria-hidden="true" style={{ color: 'var(--color-ink-faint)' }} />
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: GOLD }}>{formatDateWords(todayInput())}</div>
          <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'var(--color-ink-muted)' }}>Check-in is locked to today.</div>
        </div>

        {/* Cruise selection */}
        <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 20px', marginBottom: 24, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Select Cruise</div>
          {cruises.length === 0 ? (
            <div style={{ color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>Loading cruises…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cruises.map((c) => {
                const isSel = selectedCruise?.cruise_name === c.cruise_name;
                return (
                  <button
                    key={c.cruise_name}
                    onClick={() => setSelectedCruise(c)}
                    style={{
                      background: isSel ? GOLD : 'transparent',
                      border: `2px solid ${isSel ? GOLD : NAVY}`,
                      borderRadius: 10,
                      padding: '12px 16px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: isSel ? NAVY : NAVY }}>{c.cruise_name}</div>
                    {c.departure_time && (
                      <div style={{ fontSize: '0.78rem', color: isSel ? '#4a3800' : '#7a6a50', marginTop: 2 }}>
                        {c.departure_time}{c.end_time ? ` – ${c.end_time}` : ''}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Home-screen tip */}
        <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', textAlign: 'center', marginBottom: 16, lineHeight: 1.5 }}>
          <Pin size={14} strokeWidth={1.75} aria-hidden="true" style={{ display: 'inline-block', verticalAlign: '-2px', marginRight: 4 }} />To save to home screen, make sure the URL shows <strong>/#/checkin</strong> before tapping "Add to Home Screen".
        </div>

        {/* Start button */}
        <button
          disabled={!canStart}
          onClick={() => onStart(todayInput(), selectedCruise)}
          style={{
            width: '100%',
            background: canStart ? GOLD : '#e0d8c8',
            color: canStart ? NAVY : '#a0987a',
            border: 'none',
            borderRadius: 12,
            padding: '16px',
            fontSize: '1.1rem',
            fontWeight: 800,
            cursor: canStart ? 'pointer' : 'not-allowed',
            letterSpacing: '0.3px',
            transition: 'all 0.15s',
          }}
        >
          Start Scanning →
        </button>
      </div>
    </div>
  );
}

// ─── Main CheckIn component ───────────────────────────────────────────────────

export default function CheckIn() {
  // Access is gated by the app's Supabase staff session (see App.js), so there
  // is no separate check-in PIN — start straight on the select screen.
  const [screen, setScreen] = useState('select');
  const [scanDate, setScanDate] = useState('');
  const [scanCruise, setScanCruise] = useState(null);

  const handleStart = (date, cruise) => {
    setScanDate(date);
    setScanCruise(cruise);
    setScreen('scan');
  };

  // Return to the main app. Hash routing means changing the hash + reloading
  // drops us back onto a normal screen (still signed in — we do NOT sign out).
  const handleExit = () => {
    window.location.hash = '#/';
  };

  if (screen === 'scan') return <ScannerScreen inputDate={scanDate} cruise={scanCruise} onBack={() => setScreen('select')} />;
  return <SelectScreen onStart={handleStart} onExit={handleExit} />;
}
